import { Day, EmployeeName, Role } from './EmployeeInterface';

/**
 * Payroll Timesheet Modifications — request/response types for the
 * /admin/timesheetEdit endpoints (Milestone 1, M1-B2).
 *
 * These mirror the REAL implemented API shapes, read from the API repo:
 *   - API/src/db/timesheetsUtil.ts        (PayrollTimesheetState — the TWO state shapes)
 *   - API/src/controllers/admin/timesheetEdit.ts (submit body + history responses)
 *   - API/src/db/timesheetChangeLogUtil.ts (TimesheetChangeLogEntry incl. adminEmployeeName)
 *   - API/src/db/timesheetRevisionSnapshotUtil.ts (TimesheetRevisionSummary)
 *
 * Everything is keyed on employeeID + payPeriodID, never timesheetID — unlock
 * deletes the Timesheet row, and history has to stay findable after it is gone.
 * Dates arrive as ISO strings over JSON.
 */

// ── State (GET /admin/timesheetEdit/state) ────────────────────────────────────

/**
 * How many unlinked rows of each kind the employee has in this pay period.
 * Keys are the camelCased entity table names (NOT the brief's short names).
 * The two optional keys are ABSENT — not zero — on tenants whose database does
 * not have those tables. `total` sums the counts actually present; 0 means
 * "nothing to submit" (Submit disabled — the PAYROLL path requires ≥ 1 entity).
 */
export interface PayrollTimesheetEntryCounts {
  timeEntry: number;
  timeEntryHourly: number;
  employeeSickClaim: number;
  employeeTask: number;
  miscEmployeeTask: number;
  employeeCustomerServiceWarranty: number;
  /** Absent when EmployeeWorkOrderTask is not deployed in this tenant database. */
  employeeWorkOrderTask?: number;
  /** Absent when EmployeeOperationalTask is not deployed in this tenant database. */
  employeeOperationalTask?: number;
  total: number;
}

/** A child attestation question, shown when the parent answer matches its trigger. */
export interface TimesheetEditQuestionChild {
  timesheetQuestionID: number;
  question: string;
  question_es: string;
  answerType: number;
  order: number;
  answerTrigger: string;
}

/**
 * A tenant attestation question, delivered on the editable state response (the
 * mobile questions endpoint is employee-token-guarded, so the admin surface gets
 * them here). Mirrors API TimesheetQuestionsController.TimesheetQuestions.
 */
export interface TimesheetEditQuestion {
  timesheetQuestionID: number;
  question: string;
  question_es: string;
  answerType: number;
  order: number;
  children: TimesheetEditQuestionChild[];
}

/** The supervisor block on the editable state (all-zero/empty when unassigned). */
export interface PayrollStateSupervisor {
  employeeID: number;
  altEmployeeID: string;
  locationID: number;
  name: EmployeeName;
}

/**
 * The pay period was already submitted — there is nothing left to edit. The UI
 * routes this to the submitted review path (board / unlock), never an error.
 */
export interface PayrollTimesheetStateSubmitted {
  exists: true;
  employeeID: number;
  payPeriodID: number;
  timesheetID: number;
}

/**
 * A pay period payroll may still build and submit. Same projection the legacy
 * unsubmitted read renders (the console consumes `days` unchanged), WITHOUT its
 * Active=1 / has-a-punch predicates, plus `active`, `entryCounts`, and
 * `timesheetQuestions`. `days` always spans the full pay period — one entry per
 * day even when every collection is empty (the skeleton payroll builds into).
 */
export interface PayrollTimesheetStateEditable {
  exists: false;
  payPeriodID: number;
  employeeID: number;
  altEmployeeID: string;
  /** false = terminated/inactive employee with pending time — drives the Inactive badge. */
  active: boolean;
  role: Role;
  jobTitle: string;
  hourlyRate: number;
  name: EmployeeName;
  phoneNumber: string;
  assignedSupervisor: PayrollStateSupervisor;
  entryCounts: PayrollTimesheetEntryCounts;
  timesheetQuestions: TimesheetEditQuestion[];
  days: Day[];
}

/**
 * The two mutually exclusive state shapes, discriminated on `exists`.
 * A 404 from the endpoint means the EMPLOYEE does not exist — an
 * already-submitted period comes back as `{ exists: true }`, never as an error.
 */
export type PayrollTimesheetState = PayrollTimesheetStateSubmitted | PayrollTimesheetStateEditable;

// ── Submit (POST /admin/timesheetEdit/submit) ─────────────────────────────────

export interface PayrollSubmitTimesheetAnswer {
  timesheetQuestionID: number;
  answer: string;
}

/**
 * Payroll's submit-on-behalf body. Mirrors the mobile submit MINUS `signature`
 * and `submittedDate`/offset — the server stamps Signature='', SignatureType=
 * 'PAYROLL', the acting admin, and a server-now date in the tenant timezone.
 * The ID arrays must exactly match the server's unlinked rows (stale client → 400).
 * `editSessionID` is the AdminApp's per-sitting GUID tying the submit to the
 * change-log rows that preceded it.
 */
export interface PayrollSubmitTimesheetRequest {
  employeeID: number;
  payPeriodID: number;
  timesheetAnswers: PayrollSubmitTimesheetAnswer[];
  timeEntryIDs?: number[];
  timeEntryHourlyIDs?: number[];
  employeeTaskIDs?: number[];
  miscEmployeeTaskIDs?: number[];
  employeeOperationalTaskIDs?: number[];
  employeeCustomerServiceWarrantyTaskIDs?: number[];
  employeeSickClaimIDs?: number[];
  driveTimeEntryIDs?: number[];
  employeeWorkOrderTaskIDs?: number[];
  editSessionID?: string;
}

/** Success body of the submit (the mobile TimesheetAddResponse shape). */
export interface PayrollSubmitTimesheetResponse {
  success: boolean;
  timesheetID?: number;
  payPeriodID?: number;
}

/**
 * Error bodies from every /admin/timesheetEdit route are `{ message }` with the
 * HTTP status carrying the meaning. Notably the mid-flow-conflict 409 is
 * `{ message: "Timesheet already exists" }` — it does NOT include the new
 * timesheetID; recover by reloading state (which returns `{ exists, timesheetID }`).
 */
export interface TimesheetEditErrorResponse {
  message: string;
}

// ── Change log (GET /admin/timesheetEdit/changeLog) ───────────────────────────

/** Mirrors the API's ChangeLogAction enum (CK constraint values). */
export enum TimesheetChangeLogAction {
  Insert = 1,
  Update = 2,
  Delete = 3,
}

/**
 * Mirrors the API's ChangeLogSource. KIOSK = a worker's write made at a kiosk
 * tablet (TAS-45 / TAS-622). WEB = an office employee's own write made in the
 * Admin App employee portal. `source` on an entry stays `string` so a value
 * added server-side later still renders (as an explicit "unknown" source).
 */
export type TimesheetChangeLogSource = 'PAYROLL' | 'EMPLOYEE' | 'KIOSK' | 'WEB' | 'SYSTEM';

/**
 * One field-level change. `action` is numeric (1/2/3), not a string.
 * Insert rows (1) carry `fieldName: null` and `newValue` = a JSON image of the
 * inserted row (column name → rendered string | null; datetimes ISO UTC);
 * Delete rows (3) carry the same image in `oldValue`. Older rows may have no image.
 */
export interface TimesheetChangeLogEntry {
  timesheetChangeLogID: number;
  employeeID: number;
  payPeriodID: number;
  timesheetID: number | null;
  entityType: string;
  entityID: number;
  action: number;
  fieldName: string | null;
  oldValue: string | null;
  newValue: string | null;
  source: string;
  adminEmployeeID: number | null;
  /** Null for EMPLOYEE / KIOSK / WEB / SYSTEM rows, and for an admin row since deleted. */
  adminEmployeeName: string | null;
  editSessionID: string | null;
  createdDate: string;
}

export interface TimesheetChangeLogResponse {
  employeeID: number;
  payPeriodID: number;
  entries: TimesheetChangeLogEntry[];
}

// ── Revisions (GET /admin/timesheetEdit/revisions[/:id]) ──────────────────────

/** Mirrors the API's RevisionType enum (types 5–7 are reserved for M3/M4). */
export enum TimesheetRevisionType {
  Submit = 1,
  SupervisorApproved = 2,
  PayrollApproved = 3,
  PreUnlock = 4,
  PrePayrollEdit = 5,
  PostPayrollEdit = 6,
  PayrollProcess = 7,
}

/** Display labels for the revision types the M1/M2 UI can encounter. Types 8/9
 *  are the NPM first-level gate (one revision per individual approval — distinct
 *  audit events, not duplicates). Unknown types render as "Type N", never crash. */
export const REVISION_TYPE_LABELS: Record<number, string> = {
  [TimesheetRevisionType.Submit]: 'Submit',
  [TimesheetRevisionType.SupervisorApproved]: 'Supervisor Approved',
  [TimesheetRevisionType.PayrollApproved]: 'Payroll Approved',
  [TimesheetRevisionType.PreUnlock]: 'Pre-Unlock',
  8: 'Manager Approved',
  9: 'Approval Group Approved',
};

/**
 * One revision WITHOUT its payload (the list read never carries payloads —
 * each is a whole serialized timesheet). NOTE: the list carries creator IDs
 * only, no display names — resolve names via the change log or admin lookups.
 */
export interface TimesheetRevisionSummary {
  timesheetRevisionSnapshotID: number;
  timesheetID: number | null;
  employeeID: number;
  payPeriodID: number;
  /** Numeric RevisionType (1–7), not a string. */
  revisionType: number;
  schemaVersion: number;
  editSessionID: string | null;
  createdByAdminEmployeeID: number | null;
  createdByEmployeeID: number | null;
  createdDate: string;
}

export interface TimesheetRevisionListResponse {
  employeeID: number;
  payPeriodID: number;
  revisions: TimesheetRevisionSummary[];
}

/** One revision with its payload parsed out of PayloadJson server-side. */
export interface TimesheetRevisionDetailResponse extends TimesheetRevisionSummary {
  payload: unknown;
}

// ── Preview (GET /admin/timesheetEdit/preview) ────────────────────────────────

/**
 * The week roll-up of the pre-submission preview — the server's REAL hour
 * calculation (the same code submit runs, weekly-40h OT included on WCDP) run
 * without persisting anything. Always the exact sum of `days`.
 */
export interface TimesheetPreviewWeek {
  regularMinutes: number;
  overtimeMinutes: number;
  doubleTimeMinutes: number;
  sickMinutes: number;
  /** Raw claimed dollars (no piece pricing / fringe) — label it "Claimed", never "Pay". */
  claimedAmount: number;
}

/**
 * One calendar day of the preview — the exact `TimesheetCalcHours` row submit
 * would insert. One row per pay-period day, zero-filled, sorted ascending.
 */
export interface TimesheetPreviewDay extends TimesheetPreviewWeek {
  /** `YYYY-MM-DD` — the employee's LOCAL work date. */
  date: string;
  /** Worked time only; sick and time-off are not folded in. */
  workedMinutes: number;
}

/**
 * The pay period was already submitted — real calculated hours exist on the
 * timesheet. A 200, not an error (mirrors `GET /state` Shape A).
 */
export interface TimesheetPreviewSubmitted {
  exists: true;
  employeeID: number;
  payPeriodID: number;
  timesheetID: number;
}

/** The editable-period preview (Shape B). */
export interface TimesheetPreviewEditable {
  exists: false;
  employeeID: number;
  payPeriodID: number;
  week: TimesheetPreviewWeek;
  days: TimesheetPreviewDay[];
}

/** The two mutually exclusive preview shapes, discriminated on `exists`. */
export type TimesheetPreview = TimesheetPreviewSubmitted | TimesheetPreviewEditable;

// ── Milestone 2 — entry CRUD (§8 of the API brief) ───────────────────────────

/** The WCDP entry kinds the M2 editor exposes (locked decision 3). These are the
 *  exact API route segments: `/admin/timesheetEdit/{kind}[/:id]`. */
export type PayrollEditEntryKind =
  | 'hourly'
  | 'piece'
  | 'task'
  | 'miscTask'
  | 'cswTask'
  | 'sickClaim'
  | 'operationalTask';

/**
 * Success body of every entry CRUD call (§8.0). The entity ID rides on the
 * kind's own key (`timeEntryHourlyID`, `employeeTaskID`, …) — read it via the
 * kind registry's `idKey`. `changedFields` is PUT-only: exactly the change-
 * logged columns, `[]` for a no-op save.
 */
export interface PayrollEntryResult {
  success: boolean;
  employeeID: number;
  payPeriodID: number;
  changedFields?: string[];
  [idKey: string]: number | boolean | string[] | undefined;
}

/** POST/PUT body for `hourly` (§8.1). All four ID fields are REQUIRED integers
 *  (`requireInt`) — `0` is the "none" sentinel for a bare (no-job) punch. */
export interface PayrollHourlyEntryBody {
  employeeID?: number;
  payPeriodID?: number;
  inDateTime: string;
  inDateTimeOffset: string;
  outDateTime?: string | null;
  outDateTimeOffset?: string | null;
  type: number;
  projectID: number;
  accountCodeID: number;
  projectPhaseID: number;
  customerServiceWarrantyID: number;
  serviceOrderText?: string;
  serviceOrderID?: number | null;
  /** TAS-514 — round-tripped unchanged by the payroll hourly modal. AdminApp never
   *  resolves or re-scopes a service order (only the employee does, on mobile), so the
   *  editor's job is simply not to drop the link the punch already carries. */
  serviceOrderScopeID?: number | null;
  tempServiceOrderDescription?: string | null;
  locationName?: string;
  description?: string;
  editSessionID: string;
}

/** POST/PUT body for `piece` (§8.2). */
export interface PayrollPieceEntryBody {
  employeeID?: number;
  payPeriodID?: number;
  inDateTime: string;
  inDateTimeOffset: string;
  outDateTime?: string | null;
  outDateTimeOffset?: string | null;
  type: number;
  editSessionID: string;
}

/** POST/PUT body for `task` (§8.3 — amount is the ONLY editable field on PUT). */
export interface PayrollTaskClaimBody {
  employeeID?: number;
  payPeriodID?: number;
  taskID?: number;
  amountClaimed: number;
  entryDate?: string;
  editSessionID: string;
}

/** POST/PUT body for `miscTask` (§8.4). */
export interface PayrollMiscTaskBody {
  employeeID?: number;
  payPeriodID?: number;
  projectID?: number;
  amountClaimed: number;
  miscDescription: string;
  accountCodeID?: number;
  intermediateID?: number;
  superDescription?: string;
  entryDate?: string;
  editSessionID: string;
}

/** POST/PUT body for `cswTask` (§8.5). */
export interface PayrollCswTaskBody {
  employeeID?: number;
  payPeriodID?: number;
  customerServiceWarrantyID: number;
  projectID: number;
  minutesClaimed: number;
  amountClaimed: number;
  locationName?: string;
  description?: string;
  entryDate?: string;
  editSessionID: string;
}

/** POST/PUT body for `sickClaim` (§8.6). Omitting `hourlyAmount` on PUT leaves
 *  the stored rate untouched — send it only when setting the rate. */
export interface PayrollSickClaimBody {
  employeeID?: number;
  payPeriodID?: number;
  minutesClaimed: number;
  amountClaimed: number;
  hourlyAmount?: number;
  accountCodeID?: number;
  description?: string;
  entryDate?: string;
  editSessionID: string;
}

/** POST/PUT body for `operationalTask` (§8.7 — WCDP-only kind). The API takes
 *  HOURS and converts to stored minutes at the boundary (round(hours*60), the
 *  same conversion the mobile app performs client-side); NO amount is ever
 *  sent — the server computes the stored amount from the employee's system
 *  rate × hours and returns it on the entity row. */
export interface PayrollOperationalTaskBody {
  employeeID?: number;
  payPeriodID?: number;
  operationalTaskID: number;
  hours: number;
  description?: string;
  entryDate?: string;
  editSessionID: string;
}

/** Union body for the generic manager/dataLayer CRUD surface. */
export type PayrollEntryBody =
  | PayrollHourlyEntryBody
  | PayrollPieceEntryBody
  | PayrollTaskClaimBody
  | PayrollMiscTaskBody
  | PayrollCswTaskBody
  | PayrollSickClaimBody
  | PayrollOperationalTaskBody;

/** The contractual 409 message the CRUD endpoints return verbatim (§8.0). */
export const TIMESHEET_EDIT_CONFLICT_MESSAGE =
  'Timesheet already submitted — unlock it first. Post-approval editing arrives in a later milestone.';

/** The contractual task-budget 400 message (§8.3) — a designed UI state. */
export const TASK_BUDGET_ERROR_MESSAGE = 'Insufficient remaining amount for this task.';

// ── Per-punch history (GET /admin/timesheetEdit/punchHistory — TAS-365) ──────

/** In/out as stored: ISO UTC instants with their offsets ('-07:00'). */
export interface PunchHistoryTimes {
  in: string | null;
  inOffset: string | null;
  out: string | null;
  outOffset: string | null;
}

/**
 * One changed column, unrendered: DB column name + stored Old/New (datetimes ISO
 * UTC). In/Out changes also carry the offset in effect on each side so the
 * punch's own wall clock can be shown. Added rows list the image's key fields as
 * `new` (old null); Deleted rows as `old` (new null).
 */
export interface PunchHistoryChange {
  field: string;
  old: string | null;
  new: string | null;
  oldOffset?: string | null;
  newOffset?: string | null;
}

export type PunchHistoryAction = 'Added' | 'Edited' | 'Deleted';

export interface PunchHistoryEvent {
  /** ISO UTC. */
  at: string;
  action: PunchHistoryAction;
  /** PAYROLL | EMPLOYEE | KIOSK | WEB | SYSTEM (kept `string`: an unknown future source still renders). */
  source: string;
  /** Admin name, else the employee's own name on EMPLOYEE / KIOSK / WEB events; null otherwise. */
  actorName: string | null;
  adminEmployeeID: number | null;
  kioskDeviceID: number | null;
  /** Imported from the old Audit text by the backfill. */
  backfilled: boolean;
  changes: PunchHistoryChange[];
  /** Added / Deleted: the full row image (column → stored value). */
  image?: Record<string, string | null>;
}

export interface PunchHistoryItem {
  entityType: 'TimeEntry' | 'TimeEntryHourly';
  entityID: number;
  deleted: boolean;
  /** True when the history opens with the punch being added (nothing earlier missing). */
  complete: boolean;
  /** 'auditFallback' = best-effort read of the old Audit text (punch predates the log). */
  origin: 'changeLog' | 'auditFallback';
  original: PunchHistoryTimes;
  current?: PunchHistoryTimes;
  /** Oldest first. */
  events: PunchHistoryEvent[];
}

export interface PunchHistoryResponse {
  employeeID: number;
  payPeriodID: number;
  punches: PunchHistoryItem[];
}
